/**
 * `usePendingConfirmationCount` — THE shared tenant-wide pending-confirmation
 * count (DNKADM-0276): one lightweight size-1 read of the confirmation queue
 * (`totalElements` is the count, the page content is discarded), refetched
 * whenever a queue mutation fires the confirmation-queue-changed event.
 *
 * Both the nav badge (`AppLayout`) and Now & Next's "Needs confirmation"
 * header read this hook, so the two can never disagree about what awaits
 * confirmation: same endpoint, same event-driven refresh.
 *
 * Returns `null` until the first successful load (and after a failed one) —
 * callers fail soft (the badge hides, the board falls back to its day-window
 * count) rather than showing a fabricated zero as truth.
 */
import { useEffect } from 'react';
import { listUnconfirmedReservations } from '@/api/reservations';
import { useResource } from '@/hooks/useResource';
import { onConfirmationQueueChanged } from './confirmationQueueEvents';

export function usePendingConfirmationCount(): number | null {
  const queue = useResource(
    () => listUnconfirmedReservations({ page: 0, size: 1 }),
    [],
  );
  useEffect(() => onConfirmationQueueChanged(queue.refetch), [queue.refetch]);
  return queue.data?.totalElements ?? null;
}
